import { Component } from '@angular/core';
import { DialogRef } from '@fundamental-ngx/core/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DisplayedColumn } from '../../../core/models/misc/displayed-column.model';

enum MoveButtonType {
	Up = 'Up',
	Down = 'Down',
	Top = 'Top',
	Bottom = 'Bottom',
}

@Component({
    selector: 'app-table-personalization-dialog',
    templateUrl: './table-personalization-dialog.component.html',
    styleUrls: ['./table-personalization-dialog.component.scss'],
    standalone: false
})
export class TablePersonalizationDialogComponent {
    MoveButtonType = MoveButtonType;
    defaultColumns: DisplayedColumn[] = [];
    columns: DisplayedColumn[] = [];
    currentColumn: DisplayedColumn;
    isEmpty = false;
    isDirty = false;

    constructor(public dialogRef: DialogRef) {
        this.defaultColumns = this.dialogRef.data.defaultColumns.sort(this.sortFn);
        this.columns = this.cloneColumnsArray(this.dialogRef.data.columns).sort(this.sortFn);
        this.updateDirtyState();
    }

    handleChange(column: DisplayedColumn, checked?: boolean): void {
        column.checked = checked;
        this.isEmpty = !this.columns.some((c) => c.checked);
        this.updateDirtyState();
    }

    dropHandle(event: CdkDragDrop<string[]>): void {
        if (this.columns[event.currentIndex].disabled) {
            return;
        }
        moveItemInArray(this.columns, event.previousIndex, event.currentIndex);
        this.updateDirtyState();
    }

    isValid(): boolean {
        return !this.isEmpty;
    }

    dropListEnterPredicate(list: DisplayedColumn[]) {
        return (index: number) => {
            return !list[index].disabled;
        };
    }

    reset(): void {
        this.columns = this.cloneColumnsArray(this.defaultColumns);
        this.updateDirtyState();
    }

    cloneColumnsArray(columnsArray: DisplayedColumn[]): DisplayedColumn[] {
        return columnsArray.map((item: DisplayedColumn) => Object.assign(new DisplayedColumn(), item));
    }

    onHover(column: DisplayedColumn) {
        this.currentColumn = column;
    }

    getMinIndexForMove() {
        return this.columns.findIndex((col) => !col.disabled);
    }

    onMoveButtonPress(event: MouseEvent, buttonType: MoveButtonType, currentIndex: number): void {
        let nextIndex;
        switch (buttonType) {
        case MoveButtonType.Top:
            nextIndex = this.getMinIndexForMove();
            break;
        case MoveButtonType.Up:
            nextIndex = currentIndex - 1;
            break;
        case MoveButtonType.Down:
            nextIndex = currentIndex + 1;
            break;
        case MoveButtonType.Bottom:
            nextIndex = this.columns.length - 1;
            break;
        }
        moveItemInArray(this.columns, currentIndex, nextIndex);
        event.stopPropagation();
        this.updateDirtyState();
    }

    isMoveButtonDisabled(buttonType: MoveButtonType, index: number) {
        let minTopIndex = this.getMinIndexForMove();
        switch (buttonType) {
        case MoveButtonType.Top:
        case MoveButtonType.Up:
            return index <= minTopIndex;
        case MoveButtonType.Down:
        case MoveButtonType.Bottom:
            return index === this.columns.length - 1;
        }
        return false;
    }

    updateDirtyState() {
        this.isDirty = !(
            this.columns.length === this.defaultColumns.length &&
			this.columns.every((val, index) => this.isColumnEqual(val, this.defaultColumns[index]))
        );
    }

    isColumnEqual(a: DisplayedColumn, b: DisplayedColumn) {
        return a.key === b.key && a.checked === b.checked;
    }

    sortFn(n1: DisplayedColumn, n2: DisplayedColumn) {
        if (!n1.checked && n2.checked) {
            return 1;
        }
        if (!n2.checked && n1.checked) {
            return -1;
        }

        if (n1.checked && n2.checked) {
            return 0;
        }

        if (n1.key > n2.key) {
            return 1;
        }

        if (n1.key < n2.key) {
            return -1;
        }

        return 0;
    }
}
